/* eslint-disable */
import React, { useEffect, useState } from "react";
import firebase from "./firebaseConfig";
import { useNavigate } from "react-router-dom";
import API_ENDPOINTS from './config/api';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [apiKey, setApiKey] = useState(null);
  const [newProject, setNewProject] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingApiKey, setIsLoadingApiKey] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const navigate = useNavigate();

  // Format date helper function
  const formatDate = (dateValue) => {
    if (!dateValue) return 'Unknown';
    
    try {
      let date;
      
      // Handle Firestore timestamp format
      if (dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } 
      // Handle Firestore timestamp with nanoseconds
      else if (dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      }
      // Handle ISO string or regular date
      else {
        date = new Date(dateValue);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Unknown';
      }
      
      return date.toLocaleDateString();
    } catch (error) {
      return 'Unknown';
    }
  };

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(u => {
      if (!u) navigate("/login");
      else setUser(u);
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    // Clean up any existing widget when Dashboard loads
    delete window.SWC_API_KEY;
    delete window.SWC_PROJECT_ID;
    
    // Remove widget DOM elements
    const widgetElement = document.getElementById('ai-chat-widget');
    if (widgetElement) {
      widgetElement.remove();
    }
    
    const toggleButton = document.getElementById('ai-chat-toggle');
    if (toggleButton) {
      toggleButton.remove();
    }
    
    // Remove widget script
    const widgetScript = document.querySelector(`script[src="${API_ENDPOINTS.WIDGET_SCRIPT}"]`);
    if (widgetScript) {
      widgetScript.remove();
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadApiKeyFromBackend();
  }, [user]);

  useEffect(() => {
    if (!user || !apiKey) return;
    loadProjectsFromBackend();
  }, [user, apiKey]);

  // Track when all initial loading is complete
  useEffect(() => {
    if (!isLoadingApiKey && !isLoadingProjects) {
      setIsInitialLoading(false);
    }
  }, [isLoadingApiKey, isLoadingProjects]);

  // Load projects from backend
  const loadProjectsFromBackend = async () => {
    setIsLoadingProjects(true);
    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (apiKey) {
        headers['x-api-key'] = apiKey;
      }
      
      const response = await fetch(API_ENDPOINTS.PROJECTS(user.uid), {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects);
      } else {
        throw new Error('Failed to load projects');
      }
    } catch (error) {
      alert('Error loading projects: ' + error.message);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const loadApiKeyFromBackend = async () => {
    setIsLoadingApiKey(true);
    try {
      const response = await fetch(API_ENDPOINTS.API_KEYS(user.uid));
      
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.apiKey);
      } else if (response.status === 404) {
        // No API key exists, create one automatically
        await generateApiKey(false); // Don't show alert for auto-generation
      } else {
        throw new Error('Failed to load API key');
      }
    } catch (error) {
      // Error loading API key - continue without it
    } finally {
      setIsLoadingApiKey(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  const addProject = async (e) => {
    e.preventDefault();
    if (!newProject.trim()) return;
    
    if (/\s/.test(newProject)) {
      alert("Project name cannot contain spaces. Use underscores or hyphens instead.");
      return;
    }
    
    if (!/^[a-zA-Z0-9_-]+$/.test(newProject)) {
      alert("Project name can only contain letters, numbers, underscores, and hyphens.");
      return;
    }

    if (!apiKey) {
      alert("API key required");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(API_ENDPOINTS.PROJECTS_CREATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        },
        body: JSON.stringify({
          name: newProject,
          description: `AI-powered project: ${newProject}`
        })
      });
      
      if (response.ok) {
        setNewProject("");
        loadProjectsFromBackend(); // Reload projects from backend
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to create project');
      }
    } catch (error) {
      alert("Error creating project: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm(`Are you sure you want to delete this project?`)) return;
    
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/projects/${projectId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        loadProjectsFromBackend(); // Reload projects from backend
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete project');
      }
    } catch (error) {
      alert("Error deleting project: " + error.message);
    }
  };

  const generateApiKey = async (showAlert = true) => {
    try {
      const response = await fetch(API_ENDPOINTS.API_KEYS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user.uid,
          domain: window.location.hostname
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.apiKey);
        if (showAlert) {
          alert('API Key generated successfully!');
        }
      } else {
        const error = await response.json();
        if (showAlert) {
          alert(error.error || 'Failed to generate API key');
        }
      }
    } catch (error) {
      if (showAlert) {
        alert("Error generating API key: " + error.message);
      }
    }
  };

  // Utility function to create URL-friendly slug from project name
  const createSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim('-'); // Remove leading/trailing hyphens
  };

  const openProject = (project) => {
    // Navigate to a project-specific view using project name slug
    const slug = createSlug(project.name);
    navigate(`/project/${slug}`, { state: { project } });
  };

  const logout = async () => {
    await firebase.auth().signOut();
    localStorage.removeItem("firebase_id_token");
    navigate("/login");
  };

  const getUserInitials = (email) => {
    return email ? email.substring(0, 2).toUpperCase() : "U";
  };

  // Show big loading spinner during initial load
  if (isInitialLoading || !user) {
    return (
      <div className="page-loading">
        <div className="loading-content">
          <div className="loading-logo">SW</div>
          <div className="loading-xl" style={{ margin: '1rem 0' }}></div>
          <h2>Loading Dashboard</h2>
          <p className="loading-text">
            {!user ? 'Authenticating your session...' : 'Loading your projects and settings...'}
          </p>
          <div className="loading-steps">
            <div className={`loading-step ${!user ? 'loading' : 'completed'}`}>
              <div className="step-icon">✓</div>
              <span>Authentication</span>
            </div>
            <div className={`loading-step ${!user ? 'pending' : !apiKey ? 'loading' : 'completed'}`}>
              <div className="step-icon">✓</div>
              <span>API Key</span>
            </div>
            <div className={`loading-step ${!user || !apiKey ? 'pending' : isLoadingProjects ? 'loading' : 'completed'}`}>
              <div className="step-icon">✓</div>
              <span>Projects</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>Smart Web Chat</h1>
          <p className="small-text muted-text" style={{ margin: 0 }}>Intelligent project management system</p>
        </div>
        <div className="user-info">
          <div className="user-avatar">
            {getUserInitials(user?.email)}
          </div>
          <div>
            <div style={{ fontWeight: '500' }}>{user?.email}</div>
            <button onClick={logout} className="btn-danger btn-small" style={{ marginTop: '0.25rem' }}>Logout</button>
          </div>
        </div>
      </div>

      {/* Create Project */}
      <section className="card dashboard-section">
        <h3>Create New Project</h3>
        <p className="small-text muted-text" style={{ marginBottom: '1rem' }}>
          Project names can only contain letters, numbers, underscores, and hyphens (no spaces).
        </p>
        <form onSubmit={addProject}>
          <div className="input-row">
            <div className="field" style={{ flex: 1 }}>
              <label style={{ marginBottom: '0.5rem', display: 'block' }}>Project Name</label>
              <input
                type="text"
                value={newProject}
                onChange={(e) => setNewProject(e.target.value)}
                placeholder="my_project_name"
                required
              />
            </div>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? <span className="loading"></span> : ''} Create Project
            </button>
          </div>
        </form>
      </section>

      {/* Projects */}
      <section className="dashboard-section">
        <h3>Your Projects ({projects.length})</h3>
        {isLoadingProjects ? (
          <div className="project-grid" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="project-card" key={`skeleton-${i}`}>
                <div className="project-header">
                  <div className="skeleton skeleton-avatar" style={{ width: 48, height: 48 }} />
                  <div style={{ flex: 1 }}>
                    <div className="skeleton-line" style={{ width: '60%', height: 14, marginBottom: 8, borderRadius: 6 }} />
                    <div className="skeleton-line" style={{ width: '40%', height: 12, borderRadius: 6 }} />
                  </div>
                </div>
                <div className="skeleton-rect" style={{ width: '100%', height: 80, borderRadius: 12 }} />
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="card empty-state">
            <h3 className="muted-text">No projects yet</h3>
            <p className="small-text muted-text">Create your first project to get started!</p>
          </div>
        ) : (
          <div className="project-grid">
            {projects.map(project => (
              <div
                className="project-card"
                key={project.id}
                onClick={() => openProject(project)}
                role="button"
                tabIndex={0}
              >
                <div className="project-header">
                  <div className="project-icon">
                    {(project.name || project.id).substring(0, 2).toUpperCase()}
                  </div>
                  <div className="project-info">
                    <h3>{project.name || project.id}</h3>
                    <p className="small-text muted-text" style={{ margin: 0 }}>
                      {project.fileCount || 0} files • Created {formatDate(project.createdAt)}
                    </p>
                  </div>
                </div>
                <p className="muted-text" style={{ margin: '1rem 0' }}>{project.description}</p>
                <div className="project-actions">
                  <button onClick={(e) => { e.stopPropagation(); openProject(project); }} className="btn-primary btn-small">Open Project</button>
                  <button onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }} className="btn-danger btn-small">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* API Key Management */}
      <section className="dashboard-section">
        <div className="flex-between" style={{ marginBottom: '1rem' }}>
          <h3>Your API Key</h3>
          {!apiKey && (
            <button onClick={generateApiKey} className="btn-primary">Generate API Key</button>
          )}
        </div>

        {isLoadingApiKey ? (
          <div className="card empty-state">
            <div className="loading" style={{ margin: '0 auto 1rem' }}></div>
            <p>Loading API key...</p>
          </div>
        ) : apiKey ? (
          <div className="card">
            <div style={{ marginBottom: '1rem' }}>
              <label>API Key (use this in your widget integration):</label>
              <div className="code-inline">
                <span style={{ flex: 1 }}>{apiKey}</span>
                <button onClick={() => copyToClipboard(apiKey)} className="btn-secondary btn-small">Copy</button>
              </div>
            </div>
            <div className="status info" style={{ textAlign: 'left' }}>
              <p><strong>Integration Instructions:</strong></p>
              <p>1. Add this script to your website:</p>
              <code style={{ display: 'block', background: 'transparent', padding: 0, color: 'white' }}>
                {`<script src="${API_ENDPOINTS.WIDGET_SCRIPT}" data-api-key="${apiKey}" data-project-id="YOUR_PROJECT_ID"></script>`}
              </code>
              <p>2. Replace YOUR_PROJECT_ID with any of your project IDs above</p>
              <p>3. The widget will appear automatically on your website</p>
            </div>
          </div>
        ) : (
          <div className="card empty-state">
            <p>No API key generated yet.</p>
            <p className="small-text muted-text">Generate an API key to integrate the chat widget on your websites.</p>
          </div>
        )}
      </section>
    </div>
  );
}

export default Dashboard;
